<template>
  <div>
    <page-header>
      <template #breadcrumbs>
        <router-link :to="{ name: 'Roles', query: { page: this.$route.query.page } }">Роли</router-link> &raquo;
        <span>{{ item && item.name ? item.name : "Новая роль" }}</span>
      </template>
      <h2>Карточка роли</h2>
    </page-header>
    <div>
      <ul class="nav nav-tabs nav-justified mt-3">
        <li class="nav-item">
          <a
            class="nav-link"
            :class="{ active: isActive('tab1') }"
            href="#tab1"
            @click.prevent="setActive('tab1')"
          >
            Общая информация
          </a>
        </li>
        <li v-if="canViewPermissions || canEditPermissions" class="nav-item">
          <a
            class="nav-link"
            :class="{ active: isActive('tab2') }"
            href="#tab1"
            @click.prevent="setActive('tab2')"
          >
            Права доступа
          </a>
        </li>
      </ul>
    </div>
    <div class="card" v-if="item">
      <div id="myTabContent" v-disable-all="isReadonly" class="tab-content p-3">
        <div
          id="tab1"
          class="tab-pane fade"
          :class="{ 'active show': isActive('tab1') }"
        >
          <div class="row mt-10">
            <div class="col-md-6">
              <div class="form-group required">
                <label class="control-label">Название:</label>
                <input class="form-control required" v-model="item.name" />
                <div class="invalid-msg" v-if="v$.item.name.$error">
                  <template v-if="v$.item.name.maxLength.$invalid">
                    Максимальная длина поля
                    {{ v$.item.name.maxLength.$params.max }}
                    символов.
                  </template>
                  <template v-else>Введите данные</template>
                </div>
              </div>
            </div>
          </div>
          <div class="row mt-10">
            <div class="col-md-6">
              <div class="form-group required">
                <label class="control-label">Описание:</label>
                <textarea
                  class="form-control required"
                  v-model="item.description"
                  rows="3"
                ></textarea>
              </div>
            </div>
          </div>
          <div class="form-check my-3">
            <input
              id="chkIsDisabled"
              v-model="item.isDisabled"
              type="checkbox"
              class="form-check-input"
            />
            <label for="chkIsDisabled" class="form-check-label">
              Роль неактивна
            </label>
          </div>
        </div>
        <div
          id="tab2"
          v-if="canViewPermissions || canEditPermissions"
          class="tab-pane fade"
          :class="{ 'active show': isActive('tab2') }"
        >
          <div
            v-for="(group, key) in permissions"
            class="group"
            :key="`group-${key}`"
          >
            <span
              :class="[
                'group__header',
                { 'group__header--collapsed': !group.visible.flag },
              ]"
              @click="() => group.visible.toggle()"
            >
              <h5>
                {{ group.name }}
              </h5>
              <AtomIcon
                name="chevron"
                :class="{ 'icon--collapsed': !group.visible.flag }"
              />
            </span>
            <InputSearch
              v-if="group.visible.flag"
              v-model="group.searchText"
              placeholder="Введите данные для поиска..."
              class="group__search"
            />
            <ul v-if="group.visible.flag" class="group__permissions">
              <li>
                <label :for="`chkPerm${key}_all`" class="form-check-label">
                  <input
                    :id="`chkPerm${key}_all`"
                    :checked="isSelectedAllByGroup(key)"
                    type="checkbox"
                    class="form-check-input check-all"
                    :disabled="!canEditPermissions || isCheckAllDisabled"
                    @change="() => toggleAll(key, isSelectedAllByGroup(key))"
                  />
                  Выбрать все
                </label>
              </li>
              <li
                v-for="permission in group.items.filter((item) => {
                  return (
                    !group.searchText ||
                    item.name
                      .toUpperCase()
                      .indexOf(group.searchText.toUpperCase()) > -1
                  );
                })"
                :key="permission.id"
              >
                <label
                  :for="'chkPerm_' + permission.id"
                  class="form-check-label"
                >
                  <input
                    :id="'chkPerm_' + permission.id"
                    v-model="item.permissionIds"
                    type="checkbox"
                    class="form-check-input"
                    :true-value="[]"
                    :value="permission.id"
                    :disabled="!canEditPermissions || isPermissionDisabled(permission.id)"
                    @change="changeCheckbox(permission, $event)"
                  />
                  {{ permission.name }}
                </label>
              </li>
            </ul>
          </div>
        </div>
      </div>
      <div class="card-footer">
        <div class="float-right d-flex align-items-center">
          <button-component
            v-if="!isReadonly"
            v-on:click="save"
            type="button"
            class="btn btn-primary mr-2"
          >
            Сохранить
          </button-component>
          <button-component
            kind="secondary"
            type="button"
            class="btn btn-secondary"
            v-on:click="close"
          >
            Закрыть
          </button-component>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import PageHeader from "../../../common/components/page/PageHeader";
import API from "../../admin/api/roles";
import Constants from "../../../common/constants";
import { maxLength, required } from "@vuelidate/validators";
import { useVuelidate } from "@vuelidate/core";
import disableAll from "../../../common/directives/disable-all";
import security from "../../../common/mixins/security";
import AtomIcon from "@/common/ui/Icon.vue";
import ButtonComponent from "../../../common/ui/ButtonComponent";
import InputSearch from "../../../common/ui/InputSearch.vue";

import { booleanHook } from "@/common/hooks/booleanHook.js";

const hiddenPermissions = [
  "Редактирование настроек подключения к РМИС",
  "Просмотр сопоставления справочников РМИС",
  "Редактирование сопоставления справочников РМИС",
  "Просмотр мед. услуг",
  "Редактирование мед. услуг",
  "Просмотр записи Регистра законных представителей",
  "Редактирование записи Регистра законных представителей",
];

export default {
  name: "RoleCard",
  components: {
    PageHeader,
    AtomIcon,
    ButtonComponent,
    InputSearch,
  },
  directives: {
    "disable-all": disableAll,
  },
  mixins: [security],
  data() {
    return {
      id: this.$route.params.id,
      isReadonly: this.$route.params.action === "view",
      item: {
        permissionIds: []
      },
      activeItem: "tab1",
      permissions: [],
      permissionsSimple: [],
      canViewPermissions: false,
      canEditPermissions: false,
      permissionAccessRules: {
        [Constants.LoginMobilePatient]: {
          enable: [Constants.ViewSystemMessages, Constants.GeneralQuestionsChat, Constants.OrgServicesChat],
          ignoreIfSet: [Constants.LoginMobileOrg]
        }
      }
    };
  },
  created() {
    this.checkPermissions([Constants.RoleEdit, Constants.RoleView]);
    this.loadDictionaries();
    this.loadCard();
    this.checkPermission(Constants.PermissionsView).then((response) => {
      this.canViewPermissions = response.data;
    });
    this.checkPermission(Constants.PermissionsEdit).then((response) => {
      this.canEditPermissions = response.data;
    });
  },
  methods: {
    changeCheckbox(perm, e) {
      if (
        perm.parentId &&
        this.item?.permissionIds?.includes(perm.id) &&
        !this.item?.permissionIds?.includes(perm.parentId)
      ) {
        this.item.permissionIds.push(perm.parentId);

        //и рекурсивно вызываем для родителя
        var parent = this.permissionsSimple.find((x) => x.id == perm.parentId);
        if (parent && parent.id != perm.id) this.changeCheckbox(parent);
      }
      if (e.target.checked) {
        if (this.permissionAccessRules[perm.id]) {
          const entry = this.permissionAccessRules[perm.id];
          for (let i = this.item.permissionIds.length-1; i >= 0; i--) {
            if (this.item.permissionIds[i] === perm.id) continue;
            if (!entry.enable.includes[this.item.permissionIds]) {
              this.item.permissionIds.splice(i, 1);
            }
          }
        }
      }
    },
    isActive(menuItem) {
      return this.activeItem === menuItem;
    },
    setActive(menuItem) {
      this.activeItem = menuItem;
    },
    groupPermissions(array) {
      const result = {};
      array.forEach((item) => {
        if (!result[item.permissionGroupId]) {
          result[item.permissionGroupId] = {
            name: item.permissionGroup.name,
            items: [],
            visible: booleanHook(),
            searchText: "",
          };
        }
        result[item.permissionGroupId].items.push(item);
      });
      return result;
    },
    loadDictionaries() {
      API.loadPermissions().then((response) => {
        const permissions = response.data.items.filter(
          (x) => !x.isSystem && !hiddenPermissions.includes(x.name)
        );
        this.permissionsSimple = permissions;
        this.permissions = this.groupPermissions(permissions);
      });
    },
    loadCard() {
      if (this.id > 0) {
        API.find(this.id).then((response) => {
          this.item = response.data;
        });
      }
    },
    save() {
      this.v$.$touch();
      if (this.v$.$invalid) {
        Constants.alert.fire(
          "Ошибка",
          "Проверьте правильность заполнения полей",
          "error"
        );
        return;
      }

      if (this.item.id) {
        API.update(this.item)
          .then((res) => {
            if (res.data && res.data.success === false) {
              Constants.alert.fire("Сохранение", res.data.message, "error");
            } else {
              Constants.alert.fire(
                "Сохранение",
                "Изменения сохранены.",
                "success"
              );
            }
          })
          .catch(() => {
            Constants.alert.fire(
              "Сохранение",
              "Ошибка при сохранении изменений.",
              "error"
            );
          });
      } else {
        API.create(this.item)
          .then((res) => {
            if (res.data && res.data.success === false) {
              Constants.alert.fire("Сохранение", res.data.message, "error");
            } else {
              Constants.alert
                .fire(
                  "Сохранение",
                  "Информация о новой роли сохранена.",
                  "success"
                )
                .then(() => {
                  this.close();
                });
            }

            //Constants.alert.fire('Сохранение', 'Информация о новой роли сохранена.', 'success');
          })
          .catch(() => {
            Constants.alert.fire(
              "Сохранение",
              "Ошибка при создании роли.",
              "error"
            );
          });
      }
    },
    close() {
      this.$router.push({ name: "Roles", query: { page: this.$route.query.page } });
    },
    toggleAll(key, isSelectedAll) {
      this.item.permissionIds = [
        // Исключаем все права текущей группы
        ...this.item.permissionIds.filter(
          (id) =>
            !this.permissions[key].items.map(({ id }) => id).includes(id) ||
            (this.permissions[key].searchText &&
              this.permissions[key].items
                .filter(
                  (item) =>
                    item.name
                      .toUpperCase()
                      .indexOf(
                        this.permissions[key].searchText.toUpperCase()
                      ) === -1
                )
                .map(({ id }) => id)
                .includes(id))
        ),
        // Если текущая группа была выбрана полностью, то возращаем пустой массив
        // В противном случае добавляем все id из группы
        ...(isSelectedAll
          ? []
          : this.permissions[key].items
              .filter(
                (item) =>
                  !this.permissions[key].searchText ||
                  item.name
                    .toUpperCase()
                    .indexOf(this.permissions[key].searchText.toUpperCase()) >
                    -1
              )
              .map(({ id }) => id)),
      ];
    },
    isSelectedAllByGroup(key) {
      return this.permissions[key].items
        .filter(
          (item) =>
            !this.permissions[key].searchText ||
            item.name
              .toUpperCase()
              .indexOf(this.permissions[key].searchText.toUpperCase()) > -1
        )
        .every(({ id }) => this.item?.permissionIds?.includes(id) ?? false);
    },
    isPermissionDisabled(id) {
      for (const [key, value] of Object.entries(this.permissionAccessRules)) {
        const permId = Number(key);
        if (id === permId) continue;
        if (this.item?.permissionIds?.includes(permId)) {
          if (!value.enable.includes(id)) {
            if (!this.item?.permissionIds?.filter((x) => value.ignoreIfSet.includes(x)).length)
              return true;
          }
        }
      }
      return false;
    }
  },
  computed: {
    isCheckAllDisabled () {
      for (const [key, value] of Object.entries(this.permissionAccessRules)) {
        const permId = Number(key);
        if (this.item?.permissionIds?.includes(permId)) {
          if (!this.item?.permissionIds?.filter((x) => value.ignoreIfSet.includes(x)).length)
            return true;
        }
      }
      return false;
    }
  },
  setup: () => ({ v$: useVuelidate() }),
  validations() {
    return {
      item: {
        name: {
          required,
          maxLength: maxLength(255),
        },
      },
    };
  },
};
</script>

<style lang="scss" scoped>
@import "/src/assets/theme/default/colors.scss";

.nav-tabs {
  margin: 16px 36px 0 36px;
  justify-content: center;
  border: none;
  .nav-item {
    margin-top: 12px;
    max-width: 240px;
    min-width: 217px;
    width: 100%;
    margin-right: 8px;
    margin-left: 8px;
    .nav-link {
      padding: 14.5px 34.5px;
      text-align: center;
      font-family: "Source Sans Pro", sans-serif;
      font-style: normal;
      font-weight: 600;
      font-size: 16px;
      line-height: 120%;
      color: $gray-dark;
      border: 1px solid $gray-light1;
      box-sizing: border-box;
      border-radius: 6px;

      &.active {
        color: $blue-light;
        border: 1px solid $blue-light;
      }
    }
  }
}
.form-group.required .control-label:after {
  content: " *";
  color: red;
}
.form-check.required .control-label:after {
  content: " *";
  color: red;
}
.invalid-msg {
  color: red;
  font-size: 0.75rem;
}
.card {
  padding: 24px;
  margin: 16px 24px 0 24px;
  border-radius: 12px;
  border: none;
  box-shadow: none;
  background-color: #fff;
}

.group {
  margin-bottom: 10px;
  border: 1px solid #d7d7e0;
  box-shadow: 0px 2px 14px rgba(0, 0, 0, 0.1);
  border-radius: 12px;
  background-color: #fff;
  font-size: 16px;
  font-weight: 400;
  &__header {
    display: flex;
    justify-content: space-between;
    border-bottom: 1px solid #d7d7e0;
    cursor: pointer;
    h5 {
      font-weight: 600;
      font-size: 16px;
      color: #333333;
      text-align: left;
      padding: 22px 0 22px 24px;
      margin-bottom: 0;
    }
    &--collapsed {
      border-bottom: none;
    }
  }
  &__search {
    width: 25%;
    padding: 0 1.25rem;
  }
  &__permissions {
    padding: 0.75rem 2.5rem;
  }
  .icon {
    margin-right: 20px;
    transform: rotate(180deg);
    transition: transform 0.5s;
    &--collapsed {
      transform: rotate(0deg);
      transition: transform 0.5s;
    }
  }
}
</style>
<style lang="scss">
.form-check-input:disabled {
  background-color: #ababb3;
}
</style>
